import React from 'react';
import {useForm, Controller, useWatch} from 'react-hook-form';
import {Button, TextField, MenuItem} from '@material-ui/core';
import './WalletRestrictionForm.scss';
import {IWalletRestriction} from 'constants/interfaces';

export interface IWalletRestrictionFormSubmission {
  category: string; // subcat ID
  maxSpend: number;
}

interface WalletRestrictionFormProps {
  restriction?: {
    category: string;
    maxSpend: number;
  };

  walletBalance?: number;
  existingRestrictions?: Array<{
    restictionAmount: number;
    subCategory?: {id: number};
    categories?: {id: number};
  }>;

  // We now receive subcat objects so we can display description but store ID
  subCategories: IWalletRestriction[];

  isLoading?: boolean;
  onSubmit: (data: IWalletRestrictionFormSubmission) => void;
  onCancel: () => void;
}

export function WalletRestrictionForm(props: WalletRestrictionFormProps) {
  const {
    restriction,
    subCategories,
    isLoading,
    onSubmit,
    onCancel,
    walletBalance,
    existingRestrictions,
  } = props;

  // Sum amounts of all OTHER restrictions (excluding the category being edited)
  const alreadyAllocated = React.useMemo(() => {
    if (!existingRestrictions) return 0;
    return existingRestrictions.reduce((sum, r) => {
      const catId = String(r.subCategory?.id ?? r.categories?.id ?? '');
      // When editing, skip the current category so we don't double-count it
      if (restriction?.category && catId === restriction.category) return sum;
      return sum + (r.restictionAmount ?? 0);
    }, 0);
  }, [existingRestrictions, restriction?.category]);

  // True amount available for this restriction
  const availableBalance =
    walletBalance !== undefined ? walletBalance - alreadyAllocated : undefined;

  const defaultValues: IWalletRestrictionFormSubmission = {
    category: restriction?.category || '',
    maxSpend: restriction?.maxSpend ?? 0,
  };

  const {
    handleSubmit,
    control,
    formState: {errors},
  } = useForm<IWalletRestrictionFormSubmission>({
    defaultValues,
  });

  // Watch live value to compute remaining dynamically
  const liveMaxSpend = useWatch({control, name: 'maxSpend'});
  const liveAmount = Number(liveMaxSpend) || 0;
  const liveRemaining =
    availableBalance !== undefined ? availableBalance - liveAmount : undefined;
  const isOverBudget =
    availableBalance !== undefined && liveAmount > availableBalance;

  const isEditing = !!restriction;
  const headingText = isEditing ? 'Edit Restriction' : 'Add Restriction';
  const saveButtonText = isEditing ? 'Save Changes' : 'Save';

  const handleFormSubmit = (data: IWalletRestrictionFormSubmission) => {
    onSubmit(data);
  };

  return (
    <div className="wallet-restriction-form-container">
      <h3>{headingText}</h3>
      {walletBalance !== undefined && (
        <div style={{marginBottom: 16, color: '#666', fontSize: 14}}>
          <p style={{margin: '0 0 4px'}}>
            <strong>Total Wallet Balance:</strong> R
            {walletBalance.toLocaleString()}
          </p>
          {alreadyAllocated > 0 && (
            <p style={{margin: '0 0 4px'}}>
              <strong>Already allocated to other categories:</strong> R
              {alreadyAllocated.toLocaleString()}
            </p>
          )}
          <p
            style={{
              margin: 0,
              fontWeight: 600,
              color: isOverBudget ? '#c62828' : '#004d40',
              transition: 'color 0.2s',
            }}
          >
            Available for this restriction: R
            {(liveRemaining ?? availableBalance ?? 0).toLocaleString()}
          </p>
        </div>
      )}

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="wallet-restriction-form"
      >
        {/* Subcategory dropdown */}
        <Controller
          name="category"
          control={control}
          rules={{required: 'Category is required'}}
          render={({field}) => (
            <TextField
              {...field}
              select
              label="Category"
              variant="outlined"
              fullWidth
              margin="normal"
              disabled={isLoading}
              error={!!errors.category}
              helperText={errors.category?.message}
            >
              {subCategories.map((sub) => (
                <MenuItem key={sub.id} value={sub.id}>
                  {sub?.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* Max Spend */}
        <Controller
          name="maxSpend"
          control={control}
          rules={{
            required: 'Amount is required',
            min: {value: 1, message: 'Amount must be greater than 0'},
            validate: (value) => {
              if (
                availableBalance !== undefined &&
                Number(value) > availableBalance
              ) {
                return `Amount exceeds available budget of R${availableBalance.toLocaleString()}`;
              }
              return true;
            },
          }}
          render={({field}) => (
            <TextField
              {...field}
              label="Max Rand Spend"
              variant="outlined"
              type="number"
              fullWidth
              margin="normal"
              disabled={isLoading}
              error={!!errors.maxSpend || isOverBudget}
              helperText={
                errors.maxSpend?.message ||
                (isOverBudget
                  ? `Exceeds available budget of R${availableBalance?.toLocaleString()}`
                  : undefined)
              }
              inputProps={{
                min: 1,
                max:
                  availableBalance !== undefined ? availableBalance : undefined,
              }}
              InputProps={{
                style: isOverBudget ? {borderColor: '#c62828'} : undefined,
              }}
            />
          )}
        />

        <div className="button-row" style={{marginTop: 16}}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isLoading || isOverBudget}
            style={{marginLeft: 12}}
          >
            {isLoading ? 'Saving…' : saveButtonText}
          </Button>
        </div>
      </form>
    </div>
  );
}
